import { useCallback, useState } from 'react';
import authClient from '../client/authClient';
import { useAuth } from '../contexts/AuthContext';

/**
 * The new-person welcome on a dashboard that already holds other people's
 * work. Both inputs are facts, not settings: how many RFQs this person has
 * created (server-counted, in the dashboard summary) and when — if ever —
 * they dismissed the welcome (a timestamp on their user document, so it
 * survives a reload and follows them to another machine).
 *
 * Nothing is written on render. The only write is the dismissal, and it
 * carries the moment the person clicked.
 */
export const useDashboardWelcome = (myRfqTotal: number) => {
    const { backendUser, applyBackendUser } = useAuth();
    const [dismissing, setDismissing] = useState(false);
    const [dismissFailed, setDismissFailed] = useState(false);

    const dismiss = useCallback(async () => {
        setDismissing(true);
        setDismissFailed(false);
        try {
            const response = await authClient.dismissDashboardWelcome();
            // The write returns the updated user, so the cached copy is
            // replaced from the response rather than re-fetched.
            if (response?.user) applyBackendUser(response.user);
        } catch (error) {
            // Nothing was stored, so nothing collapses — the panel says so
            // rather than hiding and reappearing on the next load.
            console.error('Failed to record the dashboard welcome dismissal:', error);
            setDismissFailed(true);
        } finally {
            setDismissing(false);
        }
    }, [applyBackendUser]);

    return {
        /** Shown only to someone who has never created an RFQ in this org. */
        visible: myRfqTotal === 0,
        dismissed: Boolean(backendUser?.onboarding?.dashboardWelcomeDismissedAt),
        dismiss,
        dismissing,
        dismissFailed,
    };
};
